/**
 * The six realtime handlers that drive the row buffer.
 *
 * M4 deferred these because the buffer did not exist; this is the debt paid.
 * They are the only handlers in the application that do not patch a Query cache,
 * because the rows are not one -- they are a live window owned by whichever grid
 * is mounted, reached through `rowWindowRegistry`.
 *
 * Every one of them is dispatched per window rather than per table. A board is
 * one window per stack with different filters in each, so "does this row belong
 * here" has a different answer for each of them, and `rowLifecycle` is asked
 * once per window rather than once per event.
 *
 * **The acting tab does not receive its own row events.** `rows_created`,
 * `rows_updated` and `rows_deleted` all pass the requesting tab's
 * `web_socket_id` as the ignore id, so a tab that creates a row sees it because
 * it put it there, not because it was told. `row_orders_recalculated` is the
 * exception -- it passes no ignore id and reaches everybody, the tab that caused
 * it included -- which is right, because recalculating orders renumbers rows
 * nobody asked about.
 */
import type { QueryClient } from '@tanstack/react-query'

import { qk } from '@/api/query-keys'
import type { RowData } from '@/api/types'
import type { RowLifecycleContext } from '@/features/database/rows/rowLifecycle'
import { rowCreated, rowDeleted, rowUpdated } from '@/features/database/rows/rowLifecycle'
import { operationsFor } from '@/features/database/rows/rowEvents'
import {
  lifecycleContextFor,
  rowWindowsForTable,
  rowWindowsForView,
  type RowWindowMember,
} from '@/features/database/rows/rowWindowRegistry'
import { applyRowOperations } from '@/features/database/rows/rowWindowStore'
import type { RealtimeClient, RealtimeContext } from '../client'
import type { RealtimeMessage } from '../protocol'

function applyToTable(
  tableId: number,
  decide: (member: RowWindowMember, context: RowLifecycleContext) => void,
): void {
  for (const member of rowWindowsForTable(tableId)) {
    decide(member, lifecycleContextFor(member))
  }
}

/**
 * A schedule has no window to patch, so its months are invalidated instead.
 *
 * It is the one row-shaped thing in the application that lives in the Query
 * cache: its rows are days rather than a range of an ordered list, so there is
 * no index for `rowLifecycle` to decide about. Deciding *where* a changed row
 * belongs would mean re-bucketing it by the view's date column in the view's
 * timezone, which is the server's own arithmetic -- asking again is both
 * cheaper to be right about and only a request when a month is on screen.
 */
function refreshSchedules(queryClient: QueryClient, tableId: number): void {
  void queryClient.invalidateQueries({ queryKey: qk.schedulesOf(tableId) })
}

export function registerRowHandlers(client: RealtimeClient): () => void {
  const off: (() => void)[] = []
  const on = (
    type: string,
    handler: (c: RealtimeContext, d: RealtimeMessage) => void,
  ) => {
    off.push(client.on(type, handler))
  }

  on('rows_created', (context, data) => {
    const tableId = data.table_id as number
    refreshSchedules(context.queryClient, tableId)
    const rows = (data.rows ?? []) as RowData[]

    applyToTable(tableId, (member, context) => {
      for (const row of rows) {
        applyRowOperations(
          member.store,
          operationsFor(member.store.getState().window, rowCreated(row, context)),
        )
      }
    })
  })

  on('rows_updated', (context, data) => {
    const tableId = data.table_id as number
    refreshSchedules(context.queryClient, tableId)
    const rows = (data.rows ?? []) as RowData[]
    const before = (data.rows_before_update ?? []) as RowData[]

    applyToTable(tableId, (member, context) => {
      rows.forEach((row, index) => {
        // The backend can update a row nobody asked it to -- a formula
        // recalculating, a rollup following a link -- and then the two lists are
        // not the same length. An id is enough for the "was it in the view
        // before" half, and pretending otherwise would drop the event.
        const previous = before[index] ?? { id: row.id, order: row.order }
        applyRowOperations(
          member.store,
          operationsFor(
            member.store.getState().window,
            rowUpdated(previous, row, context),
          ),
        )
      })
    })
  })

  on('rows_deleted', (context, data) => {
    const tableId = data.table_id as number
    refreshSchedules(context.queryClient, tableId)
    const rows = (data.rows ?? []) as RowData[]

    applyToTable(tableId, (member, context) => {
      for (const row of rows) {
        applyRowOperations(
          member.store,
          operationsFor(member.store.getState().window, rowDeleted(row, context)),
        )
      }
    })
  })

  /**
   * Every row's `order` was renumbered.
   *
   * Nothing about the rows themselves changed, but every one of them now sorts
   * somewhere else, and the client cannot reproduce the new numbers -- they are
   * arbitrary-precision decimals the backend chose. A re-read of what is held is
   * the only honest answer.
   */
  on('row_orders_recalculated', (_context, data) => {
    for (const member of rowWindowsForTable(data.table_id as number)) {
      member.store.getState().refresh()
    }
  })

  /**
   * The server says this view's rows are stale.
   *
   * Sent when something changed that the client has no way to reproduce: a
   * formula field recalculated across the table, or a filter the backend
   * evaluates itself. Both refresh; `force_view_refresh` also carries the view
   * and its fields, and patching those caches belongs with the view handlers
   * rather than here.
   */
  const refreshView = (context: RealtimeContext, data: RealtimeMessage) => {
    for (const member of rowWindowsForView(data.view_id as number)) {
      member.store.getState().refresh()
    }
    // A schedule has no window to refresh, and this event says its rows are
    // wrong. The message names a view rather than a table, so every schedule
    // over the table goes -- which is at most a handful of cached months, and
    // only the one on screen costs a request.
    const tableId = data.table_id
    if (typeof tableId === 'number') refreshSchedules(context.queryClient, tableId)
  }

  on('force_view_rows_refresh', refreshView)
  on('force_view_refresh', refreshView)

  return () => {
    for (const remove of off) remove()
  }
}
